import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { getPaginationItems } from '@/utils/pagination';
import Breadcrumb from '@/components/common/Breadcrumb';
import Table from '@/components/admin/Table';
import communityService from '@/services/communityService';
import DropdownButton, { DropdownItem } from '@/components/ui/DropdownButton';
import { toast } from 'react-hot-toast';
import PageSizeSelect from '@/components/ui/PageSizeSelect';
import SimpleSelect from '@/components/ui/SimpleSelect';
import MabarInvoiceModal from '@/components/community/MabarInvoiceModal';
import { Button as ShadcnButton } from '@/components/shadcn/button';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100] as const;

const formatCurrency = (value: number) => {
  return `Rp. ${value.toLocaleString('id-ID')}`;
};

interface CommunityTransaction {
  id: number;
  participant_id: number;
  name: string;
  email: string;
  wa_number?: string;
  role?: string;
  event_title: string;
  event_slug: string;
  order_number: string;
  subtotal?: number;
  payment_fee?: number;
  community_receives?: number;
  total_amount?: number;
  amount: number;
  payment_method: string;
  payment_status: string;
  invoice_url: string;
  paid_at: string | null;
  created_at: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CommunityTransactionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<CommunityTransaction[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  );
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
    const limit = parseInt(searchParams.get('limit') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(limit as (typeof PAGE_SIZE_OPTIONS)[number]) ? limit : 10;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const prevSearchTermRef = useRef('');

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<CommunityTransaction | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Payment status change state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [transactionToUpdate, setTransactionToUpdate] = useState<CommunityTransaction | null>(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState<'paid' | 'unpaid'>('paid');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Invoice modal state
  const [selectedInvoiceTx, setSelectedInvoiceTx] = useState<CommunityTransaction | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  const handleOpenInvoiceModal = (tx: CommunityTransaction) => {
    setSelectedInvoiceTx(tx);
    setIsInvoiceModalOpen(true);
  };

  const loadTransactions = async (page: number = 1, search: string = '', limit?: number) => {
    const perPage = limit ?? itemsPerPage;
    setLoading(true);
    try {
      const data = await communityService.getCommunityTransactions(page, perPage, search);
      setTransactions(data.transactions || []);
      setTotalItems(data.total || 0);
    } catch (error: any) {
      toast.error(error.message || 'Gagal memuat data transaksi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchChanged = searchTerm !== prevSearchTermRef.current;
    if (searchChanged) {
      const timer = setTimeout(() => {
        prevSearchTermRef.current = searchTerm;
        setCurrentPage(1);
        updateUrlParams(1, itemsPerPage);
        loadTransactions(1, searchTerm, itemsPerPage);
      }, 500);
      return () => clearTimeout(timer);
    }

    loadTransactions(currentPage, searchTerm, itemsPerPage);
  }, [currentPage, itemsPerPage, searchTerm]);

  const updateUrlParams = (page: number, limit: number) => {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    updateUrlParams(newPage, itemsPerPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    updateUrlParams(1, newLimit);
  };

  // Delete Action handlers
  const openDeleteModal = (tx: CommunityTransaction) => {
    setTransactionToDelete(tx);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;
    setIsDeleting(true);
    try {
      await communityService.deleteParticipant(
        transactionToDelete.event_slug,
        transactionToDelete.participant_id
      );
      toast.success('Transaksi & pendaftaran member berhasil dihapus');
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
      loadTransactions(currentPage, searchTerm, itemsPerPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal menghapus transaksi');
    } finally {
      setIsDeleting(false);
    }
  };

  // Status Change Action handlers
  const openStatusModal = (tx: CommunityTransaction, status: 'paid' | 'unpaid') => {
    setTransactionToUpdate(tx);
    setNewPaymentStatus(status);
    setIsStatusModalOpen(true);
  };

  const handleConfirmPaymentStatusChange = async () => {
    if (!transactionToUpdate) return;
    setIsUpdatingStatus(true);
    try {
      await communityService.updatePaymentStatus(
        transactionToUpdate.event_slug,
        transactionToUpdate.participant_id,
        newPaymentStatus
      );
      toast.success('Status pembayaran berhasil diperbarui');
      setIsStatusModalOpen(false);
      setTransactionToUpdate(null);
      loadTransactions(currentPage, searchTerm, itemsPerPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal memperbarui status pembayaran');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getDropdownActions = (tx: CommunityTransaction): DropdownItem[] => {
    const actions: DropdownItem[] = [];

    actions.push({
      label: 'Lihat Detail Invoice',
      icon: 'material-symbols:receipt-long-outline-rounded',
      onClick: () => handleOpenInvoiceModal(tx),
    });

    const isQrisPaid = tx.payment_method?.toLowerCase() === 'qris' && tx.payment_status === 'paid';

    if (!isQrisPaid) {
      if (tx.payment_status === 'unpaid') {
        actions.push({
          label: 'Set PAID (Manual)',
          icon: 'material-symbols:check-circle-outline-rounded',
          onClick: () => openStatusModal(tx, 'paid'),
        });
      } else {
        actions.push({
          label: 'Set UNPAID',
          icon: 'material-symbols:cancel-outline-rounded',
          onClick: () => openStatusModal(tx, 'unpaid'),
        });
      }
    }

    actions.push({
      label: 'Hapus Transaksi',
      icon: 'material-symbols:delete-outline-rounded',
      destructive: true,
      onClick: () => openDeleteModal(tx),
    });

    return actions;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <Breadcrumb
          items={[{ label: 'Home', href: '/host' }, { label: 'Transaksi' }]}
        />
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daftar Transaksi Mabar</h1>
            <p className="text-sm text-gray-500 mt-1">
              Kelola pembayaran seluruh pendaftaran main bareng di komunitas Anda.
            </p>
          </div>
        </div>
      </div>

      {/* Filter and Search Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
        <div className="flex flex-row gap-3 md:gap-4 items-center justify-between">
          {/* Search bar */}
          <div className="relative flex-1 md:max-w-md">
            <Icon
              icon="material-symbols:search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5"
            />
            <input
              type="text"
              placeholder="Cari nama, email member, atau nama event..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <PageSizeSelect
              value={itemsPerPage}
              onValueChange={handleItemsPerPageChange}
              options={PAGE_SIZE_OPTIONS}
              triggerClassName="h-[38px] border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 flex flex-col items-center justify-center shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-gray-500 mt-4 font-medium">Memuat data transaksi...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 flex flex-col items-center justify-center text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
            <Icon icon="material-symbols:receipt-long-outline-rounded" className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Belum Ada Transaksi</h3>
          <p className="text-gray-500 max-w-sm mt-1">
            {searchTerm
              ? 'Tidak ditemukan transaksi yang cocok dengan kata kunci pencarian Anda.'
              : 'Seluruh riwayat pembayaran dari pendaftaran mabar berbayar akan muncul di sini.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Section */}
          <div className="hidden lg:block">
            <Table
              columns={[
                { key: 'order_number', title: 'Order Number', width: 'w-44', sortable: false },
                { key: 'name', title: 'Member / Pemain', width: 'w-60', sortable: false },
                { key: 'event_title', title: 'Event Mabar', width: 'w-60', sortable: false },
                { key: 'amount', title: 'Nominal Diterima', width: 'w-40', sortable: false },
                { key: 'payment_method', title: 'Metode', width: 'w-32', sortable: false },
                {
                  key: 'payment_status',
                  title: 'Status Pembayaran',
                  width: 'w-48',
                  sortable: false,
                },
                { key: 'created_at', title: 'Tanggal Transaksi', width: 'w-44', sortable: false },
              ]}
              data={transactions.map((tx) => ({
                id: tx.id,
                name: tx.name,
                email: tx.email,
                event_title: tx.event_title,
                event_slug: tx.event_slug,
                order_number: tx.order_number,
                amount: formatCurrency(tx.amount),
                payment_method: tx.payment_method === 'qris' ? 'QRIS' : tx.payment_method,
                payment_status: tx.payment_status,
                created_at: formatDate(tx.created_at),
                invoice_url: tx.invoice_url,
                raw: tx,
              }))}
              renderCell={(row, key) => {
                if (key === 'order_number') {
                  return <span className="font-semibold text-gray-700">{row.order_number}</span>;
                }

                if (key === 'name') {
                  return (
                    <div>
                      <div className="font-semibold text-gray-955">{row.name}</div>
                      <div className="text-xs text-gray-500 font-medium">{row.email || '-'}</div>
                    </div>
                  );
                }

                if (key === 'event_title') {
                  return (
                    <Link
                      to={`/host/mabar/${row.event_slug}/dashboard`}
                      className="text-primary-600 hover:text-primary-700 hover:underline font-semibold block max-w-xs truncate"
                      title={row.event_title}
                    >
                      {row.event_title}
                    </Link>
                  );
                }

                if (key === 'payment_method') {
                  return (
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 uppercase tracking-wide">
                      {row.payment_method}
                    </span>
                  );
                }

                if (key === 'payment_status') {
                  const status = row.payment_status as 'paid' | 'unpaid';
                  const isQrisPaid =
                    row.payment_method?.toLowerCase() === 'qris' && status === 'paid';
                  if (isQrisPaid) {
                    return (
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full uppercase">
                        Paid
                        <Icon
                          icon="material-symbols:check-circle-rounded"
                          className="w-4 h-4 text-emerald-600"
                        />
                      </span>
                    );
                  }
                  return (
                    <div className="inline-flex items-center gap-2">
                      <SimpleSelect
                        value={status}
                        onValueChange={(nextValue) =>
                          openStatusModal(row.raw, nextValue === 'paid' ? 'paid' : 'unpaid')
                        }
                        options={[
                          { value: 'paid', label: 'PAID' },
                          { value: 'unpaid', label: 'UNPAID' },
                        ]}
                        triggerClassName="h-8 px-2 text-sm bg-white"
                      />
                    </div>
                  );
                }

                return row[key];
              }}
              renderActions={(row) => {
                const items: DropdownItem[] = [];
                items.push({
                  label: 'Lihat Detail Invoice',
                  icon: 'material-symbols:receipt-long-outline-rounded',
                  onClick: () => handleOpenInvoiceModal(row.raw),
                });
                items.push({
                  label: 'Hapus Transaksi',
                  icon: 'material-symbols:delete-outline-rounded',
                  onClick: () => openDeleteModal(row.raw),
                  destructive: true,
                });
                return (
                  <DropdownButton
                    items={items}
                    triggerVariant="ghost"
                    triggerSize="sm"
                    triggerClassName="px-1 py-1"
                  />
                );
              }}
            />
          </div>

          {/* Mobile Section */}
          <div className="block lg:hidden space-y-4">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:border-gray-300 transition-all"
              >
                <div className="flex justify-between items-start gap-3 mb-3">
                  <div>
                    <h4 className="font-bold text-gray-900">{tx.name}</h4>
                    <span className="text-xs text-gray-500 block break-all">{tx.email || '-'}</span>
                  </div>
                  <DropdownButton items={getDropdownActions(tx)} />
                </div>

                <div className="space-y-2 text-sm border-t border-gray-100 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Order Number</span>
                    <span className="font-semibold text-gray-700 text-xs">{tx.order_number}</span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-gray-500 font-medium">Event Mabar</span>
                    <Link
                      to={`/host/mabar/${tx.event_slug}/dashboard`}
                      className="text-primary-600 hover:underline font-semibold text-right max-w-[200px] truncate"
                    >
                      {tx.event_title}
                    </Link>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Nominal Diterima</span>
                    <span className="font-bold text-gray-955">{formatCurrency(tx.amount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Metode</span>
                    <span className="text-xs font-bold text-blue-700 uppercase bg-blue-50 px-2 py-0.5 rounded-full">
                      {tx.payment_method === 'qris' ? 'QRIS' : tx.payment_method}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Status</span>
                    {tx.payment_method?.toLowerCase() === 'qris' && tx.payment_status === 'paid' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full uppercase">
                        Paid
                        <Icon
                          icon="material-symbols:check-circle-rounded"
                          className="w-3.5 h-3.5 text-emerald-600"
                        />
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                          tx.payment_status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        {tx.payment_status}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Tanggal</span>
                    <span className="text-gray-600 font-medium text-xs">
                      {formatDate(tx.created_at)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                  <ShadcnButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenInvoiceModal(tx)}
                    className="flex-1 border-primary-500 text-primary-600 hover:bg-primary-50 text-xs font-semibold"
                  >
                    <Icon
                      icon="material-symbols:receipt-long-outline-rounded"
                      className="w-4 h-4 text-primary-600"
                    />
                    Lihat Invoice
                  </ShadcnButton>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Section */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <span className="text-sm text-gray-600 font-medium">
              Menampilkan <span className="font-semibold text-gray-900">{transactions.length}</span>{' '}
              dari <span className="font-semibold text-gray-900">{totalItems}</span> transaksi
            </span>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Sebelumnya
                </Button>
                {getPaginationItems(currentPage, totalPages).map((item, idx) => {
                  if (item === '...') {
                    return (
                      <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400 font-bold select-none">
                        ...
                      </span>
                    );
                  }
                  const page = item as number;
                  return (
                    <button
                      key={page}
                      className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${
                        currentPage === page
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Selanjutnya
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Konfirmasi Hapus"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin menghapus transaksi{' '}
            <strong>#{transactionToDelete?.order_number}</strong> ({transactionToDelete?.name})?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Hapus
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payment Status Modal */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title="Ubah Status Transaksi"
      >
        <div className="p-6">
          <p className="text-gray-600">
            Apakah Anda yakin ingin mengubah status transaksi untuk{' '}
            <span className="font-semibold text-gray-800">{transactionToUpdate?.name}</span> menjadi{' '}
            <span className="font-bold uppercase text-primary-600">{newPaymentStatus}</span>?
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsStatusModalOpen(false)}
              disabled={isUpdatingStatus}
            >
              Batal
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmPaymentStatusChange}
              loading={isUpdatingStatus}
            >
              Simpan
            </Button>
          </div>
        </div>
      </Modal>

      {/* Mabar Invoice Detail Modal */}
      <MabarInvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        transaction={selectedInvoiceTx}
      />
    </div>
  );
};

export default CommunityTransactionPage;
